import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle, XCircle, Ticket as TicketIcon, Warning } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Public /approve/{token} page — the link emailed to managers when a form
 * with `requires_manager_approval` is submitted. No auth required; the
 * token itself is the credential.
 */
export default function Approve() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const preset = params.get("decision"); // "approve" | "reject" | null
  const [state, setState] = useState("loading"); // loading | ready | done | error | used
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [decidedAs, setDecidedAs] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BACKEND}/api/approvals/${token}`);
        setData(r.data);
        if (r.data.used) {
          setDecidedAs(r.data.decision);
          setState("used");
        } else {
          setState("ready");
        }
      } catch (e) {
        setError(e?.response?.data?.detail || "Approval link not found or has expired");
        setState("error");
      }
    })();
  }, [token]);

  const submit = async (decision) => {
    setBusy(true);
    try {
      await axios.post(`${BACKEND}/api/approvals/${token}/decide`, null, {
        params: { decision, note: note || undefined },
      });
      setDecidedAs(decision);
      setState("done");
      toast.success(decision === "approve" ? "Approved" : "Rejected");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't record decision");
    } finally {
      setBusy(false);
    }
  };

  // If the email link had ?decision=approve we still show the review page
  // (with a confirm button) rather than auto-submitting — safer against
  // accidental link previews / prefetchers.

  if (state === "loading") {
    return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  }

  if (state === "error") {
    return (
      <div className="min-h-screen grid place-items-center px-4">
        <div className="max-w-md hairline rounded-lg bg-white p-6 text-center">
          <Warning size={28} weight="duotone" className="mx-auto text-amber-500" />
          <div className="mt-2 font-heading font-bold text-lg">Link unavailable</div>
          <div className="text-sm text-slate-600 mt-1">{error}</div>
        </div>
      </div>
    );
  }

  const t = data.ticket;
  const decided = state === "done" || state === "used";

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="hairline rounded-lg bg-white p-6">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500 font-medium">
            <TicketIcon size={14} weight="duotone" className="text-[#4f4c90]" />
            {data.form_title || "Manager approval"} · Ticket #{t.number}
          </div>
          <h1 className="font-heading text-2xl font-bold mt-2 tracking-tight">{t.subject}</h1>
          <div className="text-sm text-slate-500 mt-1">
            Requested by <b className="text-slate-800">{t.requester_name}</b> — {t.requester_email}
          </div>
          {t.description && (
            <div className="mt-4 rounded-md bg-slate-50 border border-slate-200 p-3 text-sm text-slate-800 whitespace-pre-wrap">
              {t.description}
            </div>
          )}
        </div>

        {decided ? (
          <div className="hairline rounded-lg bg-white p-6 text-center" data-testid="approval-done">
            {decidedAs === "approve" ? (
              <>
                <CheckCircle size={32} weight="fill" className="mx-auto text-emerald-500" />
                <div className="font-heading text-xl font-bold mt-2">Approved</div>
                <div className="text-sm text-slate-600 mt-1">
                  {state === "used" ? "This ticket was already approved." : "Thanks — the team has been notified."}
                </div>
              </>
            ) : (
              <>
                <XCircle size={32} weight="fill" className="mx-auto text-red-500" />
                <div className="font-heading text-xl font-bold mt-2">Not approved</div>
                <div className="text-sm text-slate-600 mt-1">
                  {state === "used" ? "This ticket was already rejected." : "Thanks — we've closed the ticket and let the requester know."}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="hairline rounded-lg bg-white p-6 space-y-3" data-testid="approval-ready">
            <div className="text-sm text-slate-700">
              You&apos;ve been listed as the approver for this request.
              {preset && (
                <span className="ml-1 text-slate-500">
                  Confirm your intent to <b>{preset === "approve" ? "approve" : "reject"}</b> below.
                </span>
              )}
            </div>
            <div>
              <label className="text-xs text-slate-700">Add a comment (optional)</label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Any context you want the requester and the team to see alongside your decision."
                className="mt-1 bg-white border-slate-200 min-h-20"
                data-testid="approval-note"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                onClick={() => submit("approve")}
                disabled={busy}
                data-testid="approval-approve-btn"
                className="bg-emerald-600 hover:bg-emerald-700 gap-1 flex-1"
              >
                <CheckCircle size={14} weight="fill" /> {busy ? "Working…" : "Approve"}
              </Button>
              <Button
                onClick={() => submit("reject")}
                disabled={busy}
                data-testid="approval-reject-btn"
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50 gap-1 flex-1"
              >
                <XCircle size={14} weight="fill" /> {busy ? "Working…" : "Reject"}
              </Button>
            </div>
            <div className="text-[11px] text-slate-500 font-mono pt-2">
              This is a single-use link — you can only decide once.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
